// Search ranking. Shopify's AI search ranks loosely related products above the ones whose name matches
// what was typed ("raisins" showed prunes and apple rings, not the two raisin products; "peanuts" left
// out Butter Toffee Peanuts). So each search also runs a strict title search (`title:word`, which skips
// the AI matching) and the two result sets are merged and ordered by how well each product's name and
// type match the words typed. Ties keep Shopify's order, so popular products still lead.
// Used by the header dropdown below and by search-results-rank.js on the search page.
window.NCSearchRank = (() => {
  const STOP = new Set(['and', 'the', 'a', 'an', 'of', 'with', 'for', 'to', 'in', 'on', 'or', 'by', 'from']);
  const GIFT_TYPES = new Set(['gift baskets', 'gift towers', 'snack boxes']);
  const GIFT_WORDS = new Set(
    ('gift basket tower tray box crate hamper bouquet arrangement assortment care package present corporate occasion ' +
      'birthday sympathy condolence thank appreciation holiday christmas hanukkah chanukah easter passover purim rosh ' +
      'valentine love heart halloween congratulation congrat well father mother mom dad wedding anniversary')
      .split(' ')
  );

  const stem = (w) => {
    if (w.length > 4 && w.endsWith('ies')) return w.slice(0, -3) + 'y';
    if (w.length > 4 && /(s|x|z|ch|sh|o)es$/.test(w)) return w.slice(0, -2);
    if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
    return w;
  };
  const words = (text) =>
    (text || '')
      .toLowerCase()
      .replace(/['’]/g, '')
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  const queryWords = (query) => words(query).filter((w) => !STOP.has(w));

  // Strict title searches for a query: all words, plus all but the last while it may still be half typed.
  function keywordQueries(query, lastWordMayBePartial) {
    if (query.includes(':')) return [];
    const q = queryWords(query);
    if (!q.length) return [];
    const toQuery = (ws) => ws.map((w) => `title:${w}`).join(' ');
    return lastWordMayBePartial && q.length > 1 ? [toQuery(q), toQuery(q.slice(0, -1))] : [toQuery(q)];
  }

  // Orders elements carrying data-search-title / -type / -tags. Earlier elements win ties, so pass
  // Shopify's results first and the strict-title extras after them.
  function order(elements, query, lastWordMayBePartial) {
    const terms = queryWords(query).map(stem);
    if (!terms.length) return elements;
    const giftQuery = terms.some((t) => GIFT_WORDS.has(t));
    const has = (set, t, i) =>
      set.has(t) || (lastWordMayBePartial && i === terms.length - 1 && [...set].some((w) => w.startsWith(t)));
    const all = (set) => terms.every((t, i) => has(set, t, i));

    // Only products matching every word typed move up; the rest keep Shopify's order.
    const scored = elements.map((el, index) => {
      const title = el.dataset.searchTitle;
      if (title === undefined) return { el, index, tier: 0, giftMismatch: 0 };
      const type = el.dataset.searchType || '';
      // The product's name is the title before its descriptor: "Raw Almonds, Sliced" -> "Raw Almonds",
      // "Round Wooden ... Gift Tray — Kiwi, ..., Almonds" -> "Round Wooden ... Gift Tray".
      const name = title.split(/ [—–-] |[,(+]/)[0];
      const nameSet = new Set([...words(name), ...words(type)].map(stem));
      const titleSet = new Set([...words(title), ...words(type)].map(stem));
      const allSet = new Set([...titleSet, ...words(el.dataset.searchTags).map(stem)]);
      const tier = all(nameSet) ? 3 : all(titleSet) ? 2 : all(allSet) ? 1 : 0;
      // Among the matches, gifts follow the plain products unless the search is for a gift (and the
      // other way round).
      const isGift = GIFT_TYPES.has(type.toLowerCase());
      return { el, index, tier, giftMismatch: tier && isGift !== giftQuery ? 1 : 0 };
    });
    scored.sort((a, b) => b.tier - a.tier || a.giftMismatch - b.giftMismatch || a.index - b.index);
    return scored.map((s) => s.el);
  }

  return { keywordQueries, order };
})();

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Sold-out products show after the in-stock ones, like everywhere else on the site (owner, 2026-10-21).
    const request = (query, types = '') =>
      fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(query)}${types}` +
          `&resources[options][unavailable_products]=last&section_id=predictive-search`,
        { signal: this.abortController.signal }
      );
    const parse = (text) =>
      new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
    const titleSearches = NCSearchRank.keywordQueries(searchTerm, true).map((query) =>
      request(query, '&resources[type]=product')
        .then((response) => (response.ok ? response.text() : ''))
        .catch(() => '')
    );

    Promise.all([
      request(searchTerm).then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      }),
      ...titleSearches,
    ])
      .then(([text, ...titleTexts]) => {
        const results = parse(text);
        // The all-words title search, or the one without the half-typed last word if that found nothing.
        const titleResults = titleTexts.map((t) => t && parse(t)).find((r) => r?.querySelector('[data-search-title]'));
        if (titleResults) this.mergeTitleMatches(results, titleResults, searchTerm);
        const resultsMarkup = results.innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  // Adds the strict-title matches to Shopify's product list and orders it (see NCSearchRank above).
  mergeTitleMatches(results, titleResults, searchTerm) {
    const titleList = titleResults.querySelector('#predictive-search-results-products-list');
    let list = results.querySelector('#predictive-search-results-products-list');
    if (!list) {
      // Shopify found no products: use the product block of the title search.
      const wrapper = results.querySelector('#predictive-search-results-groups-wrapper');
      if (wrapper) {
        const groups = wrapper.querySelectorAll(':scope > .predictive-search__result-group');
        let group = groups[1];
        if (!group) {
          group = document.createElement('div');
          group.className = 'predictive-search__result-group';
          wrapper.append(group);
        }
        group.prepend(titleList.parentElement);
        wrapper.classList.remove('predictive-search__results-groups-wrapper--no-products');
      } else {
        results
          .querySelector('#predictive-search-results')
          .prepend(titleResults.querySelector('#predictive-search-results-groups-wrapper'));
      }
      list = titleList;
    }

    const items = [...list.children];
    const shown = new Set(items.map((li) => li.dataset.searchHandle));
    const extras = [...titleList.children].filter((li) => !shown.has(li.dataset.searchHandle));
    const ranked = NCSearchRank.order([...items, ...extras], searchTerm, true).slice(0, 10);
    ranked.forEach((li, i) => (li.id = `predictive-search-option-product-${i + 1}`));
    list.replaceChildren(...ranked);
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
